import React, { useEffect, useMemo, useState } from 'react';
import { Dialog, DialogContainer, Flex, ProgressCircle, ToastQueue, View } from '@adobe/react-spectrum';
import { useDependency } from '../../../../contexts/DependencyProvider';
import { ListDocumentCategoryRequest } from '../../../../services/soap/document_library/requests/ListDocumentCategoryRequest';
import DocumentUpload from '../../../../components/protected/layout-components/DocumentComponent/DocumentUpload';

interface ICreateIssueDocumentUploadDialogProps {
  isOpen: boolean;
  context?: {
    activityId?: string;
    projectId?: string;
    folderId?: string;
  };
  onClose: () => void;
  onUploaded: (documents: Array<{ documentId: string; name: string }>) => Promise<void> | void;
}

const buildCategoryTree = (data: any[]) => {
  const map: Record<string, any> = {};
  const roots: any[] = [];

  data.forEach((item: any) => {
    map[item.id] = { ...item, subChild: [] };
  });

  data.forEach((item: any) => {
    if (item.parent && map[item.parent]) {
      map[item.parent].subChild.push(map[item.id]);
    } else if (map[item.id]) {
      roots.push(map[item.id]);
    }
  });

  return roots;
};

const getFirstCategoryId = (items: any[]): string => {
  if (!items.length) return '';
  const first = items[0];
  if (!first) return '';
  if (first.id) return String(first.id);
  if (Array.isArray(first.subChild) && first.subChild.length > 0) {
    return getFirstCategoryId(first.subChild);
  }
  return '';
};

const CreateIssueDocumentUploadDialog: React.FC<ICreateIssueDocumentUploadDialogProps> = ({
  isOpen,
  context,
  onClose,
  onUploaded,
}) => {
  const { documentLibraryService, store } = useDependency();
  const [isLoading, setIsLoading] = useState(false);
  const [categoryTree, setCategoryTree] = useState<any[]>([]);

  useEffect(() => {
    let isActive = true;

    const loadCategories = async () => {
      if (!isOpen) return;
      setIsLoading(true);
      try {
        const response: any = await documentLibraryService.listDocumentCategory(
          new ListDocumentCategoryRequest(store.Server, store.SessionId)
        );
        const categories = Array.isArray(response?.item)
          ? response.item
          : Array.isArray(response)
            ? response
            : [];

        if (isActive) {
          setCategoryTree(buildCategoryTree(categories));
        }
      } catch {
        if (isActive) {
          setCategoryTree([]);
        }
      } finally {
        if (isActive) {
          setIsLoading(false);
        }
      }
    };

    loadCategories();
    return () => {
      isActive = false;
    };
  }, [documentLibraryService, isOpen, store.Server, store.SessionId]);

  const defaultCategoryId = useMemo(() => getFirstCategoryId(categoryTree), [categoryTree]);

  const treeHandle = useMemo(() => {
    const activityId = String(context?.activityId || '').trim();
    if (activityId && activityId !== '0') return activityId;

    const projectId = String(context?.projectId || '').trim();
    if (projectId && projectId !== '0') return projectId;

    const folderId = String(context?.folderId || '').trim();
    if (folderId && folderId !== '0') return folderId;

    const userContactTreeHandle = String(store?.session?.organization?.USERCONTACT?.treeHandle || '').trim();
    if (userContactTreeHandle && userContactTreeHandle !== '0') return userContactTreeHandle;

    return '';
  }, [context?.activityId, context?.folderId, context?.projectId, store?.session?.organization?.USERCONTACT?.treeHandle]);

  const selectedItem = useMemo(
    () => ({
      id: treeHandle,
    }),
    [treeHandle]
  );

  const handleUploadedDocuments = async (documents: Array<{ documentId: string; name: string }>) => {
    await onUploaded(documents);
    ToastQueue.positive('Document uploaded successfully.', { timeout: 2000 });
  };

  return (
    <DialogContainer onDismiss={onClose}>
      {isOpen ? (
        isLoading ? (
          <Dialog>
            <Flex width="100%" justifyContent="center" marginTop={10}>
              <ProgressCircle aria-label="Loading document categories" isIndeterminate />
            </Flex>
          </Dialog>
        ) : !treeHandle ? (
          <Dialog>
            <View padding="size-200">
              Upload is unavailable because no valid upload node is available for this issue.
            </View>
          </Dialog>
        ) : (
          <View>
            <DocumentUpload
              categoryId={defaultCategoryId}
              documentCategoryData={categoryTree}
              getDocumentsData={() => {}}
              close={onClose}
              selectedItem={selectedItem as any}
              onUploadedDocuments={handleUploadedDocuments}
            />
          </View>
        )
      ) : null}
    </DialogContainer>
  );
};

export default CreateIssueDocumentUploadDialog;
